/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import axios from 'axios';
import _ from 'lodash';
import urlJoin from 'proper-url-join';

import { PaginatedResult, StoreQueryParams } from '../../../../ts';
import { ReactivateSession } from '../../../../ts/session';
import { Skill, SkillType, WorkspaceStatus } from '../../../../ts/skill';
import { getAccessPrefixedUrl } from '../../util/access-control';
import { endpoints } from '../../util/config';
import { filterUserLabels } from './user-labels';

export interface PaginatedSkills extends PaginatedResult {
  skills: Skill[];
}

const auditParams: StoreQueryParams = {
  include_audit: true,
  verbose: true
};

function baseUrl(
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  skill?: Partial<Skill>
): string {
  let url: string;
  if (SkillType.SEARCH === skill?.type) {
    url = `${endpoints.store}/search`;
    if (useNewIAUpdatedAccess) {
      url = urlJoin(
        url,
        'assistants',
        assistantId,
        'environments',
        skill?.environment_id,
        'skills'
      );
    }
  } else {
    url = urlJoin(
      endpoints.store,
      getAccessPrefixedUrl(useNewIAUpdatedAccess, assistantId, 'skills')
    );
  }
  return urlJoin(url, skill?.skill_id);
}

export const listSkills = async (
  params: StoreQueryParams & { assistant_id?: string },
  useNewIAUpdatedAccess: boolean,
  assistantId: string
): Promise<PaginatedSkills> => {
  const response = await axios.get<PaginatedSkills>(
    baseUrl(useNewIAUpdatedAccess, assistantId),
    {
      params: {
        ...auditParams,
        sort: 'name',
        page_limit: 500,
        ...params
      }
    }
  );
  return response.data;
};

export const createSkill = async (
  skill: Partial<Skill>,
  params: StoreQueryParams = {}
) => {
  const data = _.omit(skill, 'skill_id');
  //Unused for Action/Dialog - may need for Search
  const response = await axios.post(
    baseUrl(false, '', {
      type: skill.type
    }),
    data,
    {
      params: {
        ...auditParams,
        ...params
      }
    }
  );
  return response.data;
};

export const deleteSkill = async (
  skill_id: string,
  params: StoreQueryParams = {}
): Promise<void> => {
  //Unused for Action/Dialog - may need for Search
  const response = await axios.delete(baseUrl(false, '', { skill_id }), {
    params: { ...auditParams, ...params }
  });
  return response.data;
};

export const updateSkill = async (
  skill: Partial<Skill>,
  params: StoreQueryParams,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
): Promise<Skill> => {
  const response = await axios.post<Skill>(
    baseUrl(useNewIAUpdatedAccess, assistantId, skill),
    skill,
    {
      params: { ...auditParams, ...params }
    }
  );

  return response.data;
};

export const getSkill = async (
  skill_id: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string,
  params?: StoreQueryParams & { export: boolean }
): Promise<Skill> => {
  // This GET call does not need AC updates
  const response = await axios.get<Skill>(
    baseUrl(useNewIAUpdatedAccess, assistantId, { skill_id }),
    {
      params: { ...auditParams, ...params }
    }
  );
  return response.data;
};
/**
 * Imports or replace the skill.
 */
export const importSkill = async (
  skill_id: string,
  data: any,
  config: { params: StoreQueryParams; onProgress?: (progress: number) => void },
  useNewIAUpdatedAccess: boolean,
  assistantId: string
): Promise<Skill> => {
  const { params, onProgress } = config ?? {};

  //NOTE this url format not compatible with getAccessPrefixedUrl
  const url = useNewIAUpdatedAccess
    ? urlJoin(endpoints.content, 'assistants', assistantId, 'skills', skill_id)
    : urlJoin(endpoints.content, 'skills', skill_id);

  const importedSkill = await axios
    .post(url, data, {
      headers: { 'Content-Type': 'multipart/form-data', version: 'v2' },
      params: { ...auditParams, ...params },
      onUploadProgress: e => {
        if (e.total && onProgress) {
          onProgress(Math.floor((e.loaded / e.total) * 100));
        }
      }
    })
    .then(response => response.data);

  // Delete the imported status since we will get it from the API directly
  delete importedSkill.status;
  const skill = await getSkill(
    importedSkill.skill_id,
    useNewIAUpdatedAccess,
    assistantId
  );
  // Merge imported data with API data because the API doesn't returned imported data right away
  return { ...skill, ...importedSkill };
};

const listAllNodesPurely = async (id: string, cursor?: string) => {
  const params = cursor ? { cursor } : {};
  //This GET call does not need AC updates
  const response = axios
    .get(urlJoin(baseUrl(false, ''), id, 'workspace', 'dialog_nodes'), {
      params
    })
    .then(({ data }) => {
      if (data.pagination.next_cursor) {
        return listAllNodesPurely(id, data.pagination.next_cursor) //recursive call for next page
          .then(nextNodes => data.dialog_nodes.concat(nextNodes));
      }
      return data.dialog_nodes;
    });

  return response;
};

export const listUserLabels = async (id: string): Promise<string[]> => {
  const nodes = await listAllNodesPurely(id);
  return filterUserLabels(nodes);
};

export const getSkillStatus = async (
  id: string,
  useNewIAUpdatedAccess: boolean,
  assistantId: string
): Promise<WorkspaceStatus> => {
  //This GET call does not need AC updates
  const response = await axios.get(
    urlJoin(
      baseUrl(useNewIAUpdatedAccess, assistantId),
      id,
      'workspace',
      'status',
      {
        query: {
          reactivateSession: ReactivateSession.NEVER
        }
      }
    )
  );
  return response.data;
};
